import * as React from 'react';
import { memoize } from '@cian/newbuilding-utils';
import { Chips } from '@cian/ui-kit';

import { PromoSearchTypeWrapper } from '../../components/PromoSearchTypeWrapper';

import { createMortgagePromoSearchTypeDescriptors } from '../../constants/descriptors';
import { isDescriptorActive } from '../../utils/filters';
import { connectToDescriptors, IDescriptorProps } from '../сonnectToDescriptors';
import { TTypedThunkDispatch } from '../../types/thunk';
import { getOffersCount } from '../../actions/offersData';
import { updateTags } from '../../actions/filterTags';

export interface IMortgagePromoSearchTypeProps extends IDescriptorProps<string> {
  getOffersCount(): void;
  updateTags(): void;
}

const SHOW_LIMIT = 3;

export const MortgagePromoSearchTypeComponent: React.FC<IMortgagePromoSearchTypeProps> = React.memo(
  ({ actionCreator, getOffersCount, updateTags, jsonQuery, descriptors }) => {
    const [opened, setOpened] = React.useState(false);

    const options = React.useMemo(
      () =>
        descriptors.map(({ state, label }) => ({
          label,
          value: state,
        })),
      [descriptors],
    );

    const displayedOptions = React.useMemo(() => (opened ? options : options.slice(0, SHOW_LIMIT)), [opened, options]);

    const activeDescriptors = descriptors.filter(descriptor => isDescriptorActive(descriptor)(jsonQuery));
    const currentValues = React.useMemo(() => activeDescriptors.map(d => d.state), [activeDescriptors]);

    const handleChipClick = React.useCallback(
      (value: string) => {
        // oxlint-disable-next-line typescript/no-non-null-assertion
        actionCreator(descriptors.find(d => d.state === value)!)();
        updateTags();
        getOffersCount();
      },
      [actionCreator, descriptors, getOffersCount, updateTags],
    );

    const handleMoreButtonClick = React.useCallback(() => {
      setOpened(true);
    }, []);

    return (
      <PromoSearchTypeWrapper
        hasMore={options.length > SHOW_LIMIT && !opened}
        onMoreButtonClick={handleMoreButtonClick}
        dataTestId="mortgage-chips-toggler"
      >
        <Chips
          options={displayedOptions}
          size="M"
          value={currentValues}
          selectType="multiple"
          onChipClick={handleChipClick}
        />
      </PromoSearchTypeWrapper>
    );
  },
);

MortgagePromoSearchTypeComponent.displayName = 'MortgagePromoSearchTypeComponent';

function mapDispatchToProps(dispatch: TTypedThunkDispatch) {
  return {
    getOffersCount: () => {
      dispatch(getOffersCount());
    },
    updateTags: () => {
      dispatch(updateTags());
    },
  };
}

export const MortgagePromoSearchType = connectToDescriptors(
  memoize(createMortgagePromoSearchTypeDescriptors),
  mapDispatchToProps,
)(MortgagePromoSearchTypeComponent);
